import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import validateInput from "@cores/validateInput";
import { LIMIT_CHAR } from "@constants";
import { IconAdd, IconCancel, IconErrorCircle, IconWarningTriangle } from "@mzc-cloudplex/icons";
import { Grid, TextField, Divider, IconButton, Stack, Button, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";

const AttributionInputAreaV2 = ({
    values = [],
    onChangeKey,
    onChangeValue,
    onClickAddAttribution,
    onClickRemoveAttribution,
    type,
    validationErrorCheck = true,
    size = "medium",
}) => {
    const { t } = useTranslation();
    const [validationErrors, setValidationErrors] = useState({});

    const filterNullValues = useCallback((validationErrorsObj) => {
        const filtered = {};
        Object.keys(validationErrorsObj).forEach((key) => {
            if (validationErrorsObj[key] !== null && validationErrorsObj[key] !== undefined) {
                filtered[key] = validationErrorsObj[key];
            }
        });
        // 빈 객체면 null 반환
        return Object.keys(filtered).length === 0 ? null : filtered;
    }, []);

    const validate = useCallback((str, values, isKey) => {
        const validationError = validateInput(str, {
            required: true,
            maxLength: LIMIT_CHAR.attribution,
            uniqueIn: isKey && values,
        });

        return validationError;
    }, []);

    const getValidationError = useCallback(
        (key) => {
            return validationErrorCheck ? validationErrors[key] || {} : {};
        },
        [validationErrors, validationErrorCheck],
    );

    // 검증 레벨(error/warning)에 따라 입력 우측에 표시할 아이콘을 반환한다. (components_v2/text-field 컨벤션과 동일)
    const getValidationIcon = useCallback(
        (key) => {
            const level = getValidationError(key).level;
            if (level === "error") return <IconErrorCircle color={"error"} size={16} />;
            if (level === "warning") return <IconWarningTriangle color={"warning"} size={16} />;
            return null;
        },
        [getValidationError],
    );

    const updateOtherKeys = useCallback(
        (id, key, values) => {
            let updateTarget = {};
            const keyElements = document.getElementsByClassName("key");

            Array.prototype.forEach.call(keyElements, (keyObj) => {
                const keyId = Number(keyObj.getAttribute("data-order"));

                if (id !== keyId) {
                    const keyValue = keyObj.getAttribute("value");
                    const validationError = validate(keyValue, values, true);
                    Object.assign(updateTarget, { [`attribution-${keyId}-key`]: validationError });
                }
            });

            return updateTarget;
        },
        [validate],
    );

    const getOrder = useCallback((keyClasses, id) => {
        const orders = [];

        Array.prototype.forEach.call(keyClasses, (keyObj) => {
            const order = Number(keyObj.getAttribute("data-order"));
            orders.push(order);
        });

        orders.sort((a, b) => a - b);

        return orders.indexOf(Number(id));
    }, []);

    useEffect(() => {
        if (!values || values.length === 0) {
            setValidationErrors({});
        } else {
            let newValidationErrors = {};
            values.forEach((value, index) => {
                const id = index;

                newValidationErrors[`attribution-${id}-key`] = validate(
                    value.key,
                    values.map(({ key }) => key),
                    true,
                );
                newValidationErrors[`attribution-${id}-value`] =
                    type !== "METADATA_TEMPLATE" ? validate(value.value) : undefined;

                onChangeValue(id, value.value, validate(value.value));
            });

            setValidationErrors(newValidationErrors);
        }
    }, []);

    useEffect(() => {
        if (!values || values.length === 0) {
            setValidationErrors((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        }
    }, [values]);

    const handleChangeKey = useCallback(
        (id, event) => {
            const key = event.target.value;
            const keyClasses = document.getElementsByClassName("key");
            const keyValues = values.map((v) => v.key);
            keyValues[getOrder(keyClasses, id)] = key;

            let updateTarget = updateOtherKeys(id, keyClasses, keyValues);
            Object.assign(updateTarget, { [`attribution-${id}-key`]: validate(key, keyValues, true) });

            setValidationErrors((prev) => {
                const newValidationErrors = {
                    ...prev,
                    ...updateTarget,
                };

                onChangeKey(id, key, filterNullValues(newValidationErrors));

                return newValidationErrors;
            });
        },
        [values, validate, getOrder, updateOtherKeys, onChangeKey],
    );

    const handleChangeValue = useCallback(
        (id, event) => {
            const validationError = type !== "METADATA_TEMPLATE" ? validate(event.target.value) : undefined;

            setValidationErrors((prev) => {
                const newValidationErrors = {
                    ...prev,
                    [`attribution-${id}-value`]: validationError,
                };

                onChangeValue(id, event.target.value, filterNullValues(newValidationErrors));

                return newValidationErrors;
            });
        },
        [type, validate, onChangeValue],
    );

    const handleAddAttribution = useCallback(() => {
        const id = !values || values.length === 0 ? 0 : values[values.length - 1].id + 1;

        setValidationErrors((prev) => {
            const newValidationErrors = {
                ...prev,
                [`attribution-${id}-key`]: validate(null),
                [`attribution-${id}-value`]: type !== "METADATA_TEMPLATE" ? validate(null) : undefined,
            };

            onClickAddAttribution(id, filterNullValues(newValidationErrors));

            return newValidationErrors;
        });
    }, [values, validate, type, onClickAddAttribution]);

    const handleRemoveAttribution = useCallback(
        (id) => {
            setValidationErrors((prev) => {
                const newValidationErrors = { ...prev };
                const validationErrorKeys = Object.keys(newValidationErrors);

                validationErrorKeys.forEach((k) => {
                    if (k.indexOf(`attribution-${id}`) > -1) {
                        delete newValidationErrors[k];
                    }
                });

                const remainingKeyValues = values.filter((v) => v.id !== id).map((v) => v.key);
                const keyClasses = document.getElementsByClassName("key");

                const updateTarget = updateOtherKeys(id, keyClasses, remainingKeyValues);
                Object.assign(newValidationErrors, updateTarget);

                onClickRemoveAttribution(id, filterNullValues(newValidationErrors));

                return newValidationErrors;
            });
        },
        [onClickRemoveAttribution, values, updateOtherKeys],
    );

    const isAddButtonDisabled = useMemo(() => {
        return validationErrorCheck && Object.values(validationErrors).filter((v) => v).length > 0;
    }, [validationErrors, validationErrorCheck]);

    // 경고(warning) 레벨 메시지는 컬럼별 helperText가 아니라 영역 하단에 1개만 표시한다. (디자인 empty-validated 상태)
    const warningMessage = useMemo(() => {
        if (!validationErrorCheck) return null;
        const messages = Object.values(validationErrors)
            .filter((v) => v && v.level === "warning" && v.message)
            .map((v) => v.message);
        return [...new Set(messages)][0] ?? null;
    }, [validationErrors, validationErrorCheck]);

    return (
        <>
            <Stack direction={"column"} gap={theme.spacing(1.5)}>
                {values &&
                    values.map((v, i) => {
                        const attributionId = v.id ?? i;
                        return (
                            <Grid container key={i} spacing={1} alignItems="flex-start">
                                <Grid item xs>
                                    <TextField
                                        fullWidth
                                        size={size}
                                        label={t("common::label::Key")}
                                        value={v.key}
                                        placeholder={t("common::label::Enter key")}
                                        inputProps={{
                                            "data-order": v.id,
                                            className: "key",
                                        }}
                                        InputLabelProps={{
                                            shrink: true,
                                            sx: {
                                                backgroundColor: theme.palette.background.paper,
                                            },
                                        }}
                                        onChange={(e) => handleChangeKey(attributionId, e)}
                                        error={getValidationError(`attribution-${attributionId}-key`).level === "error"}
                                        helperText={
                                            getValidationError(`attribution-${attributionId}-key`).level === "error"
                                                ? getValidationError(`attribution-${attributionId}-key`).message
                                                : undefined
                                        }
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                height: 40,
                                                borderRadius: "2px",
                                                pl: "16px",
                                                pr: "12px",
                                            },
                                            "& .MuiOutlinedInput-input": { p: 0 },
                                        }}
                                        InputProps={{
                                            endAdornment: getValidationIcon(`attribution-${attributionId}-key`),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs>
                                    <TextField
                                        fullWidth
                                        size={size}
                                        label={t("common::label::Value")}
                                        value={v.value}
                                        placeholder={t("common::label::Enter value")}
                                        onChange={(e) => handleChangeValue(attributionId, e)}
                                        InputLabelProps={{
                                            shrink: true,
                                            sx: {
                                                backgroundColor: theme.palette.background.paper,
                                            },
                                        }}
                                        error={
                                            getValidationError(`attribution-${attributionId}-value`).level === "error"
                                        }
                                        helperText={
                                            getValidationError(`attribution-${attributionId}-value`).level === "error"
                                                ? getValidationError(`attribution-${attributionId}-value`).message
                                                : undefined
                                        }
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                height: 40,
                                                borderRadius: "2px",
                                                pl: "16px",
                                                pr: "12px",
                                            },
                                            "& .MuiOutlinedInput-input": { p: 0 },
                                        }}
                                        InputProps={{
                                            endAdornment: getValidationIcon(`attribution-${attributionId}-value`),
                                        }}
                                    />
                                </Grid>

                                <Grid item sx={{ height: "40px", display: "flex", alignItems: "center" }}>
                                    <IconButton
                                        circled
                                        size="xsmall"
                                        onClick={() => handleRemoveAttribution(attributionId)}
                                    >
                                        <IconCancel size={12} />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        );
                    })}
            </Stack>
            <Divider sx={{ mt: theme.spacing(1.5), mb: theme.spacing(1) }} />
            {warningMessage && (
                <Grid container alignItems={"center"} gap={1} sx={{ mb: theme.spacing(1) }}>
                    <IconWarningTriangle color={"warning"} size={16} />
                    <Typography variant={"body2"} sx={{ color: theme.palette.warning.main }}>
                        {warningMessage}
                    </Typography>
                </Grid>
            )}
            <Grid container sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                <Button
                    variant={"text"}
                    color={"primary"}
                    size={"small"}
                    disabled={isAddButtonDisabled}
                    onClick={handleAddAttribution}
                    startIcon={<IconAdd size={16} />}
                    sx={{ height: 32, px: 1 }}
                >
                    {t("common::label::Add attribute", "Add attribute")}
                </Button>
            </Grid>
        </>
    );
};

export default AttributionInputAreaV2;
